import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createRootRouteWithContext,
  HeadContent,
  redirect,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type { ComponentType } from "react";

import { type AuthCtx } from "~/contexts/auth_query";

const resolvePath = (
  canExecuteAnyAdminOperation: () => boolean,
  activeClientId: string | null,
  path: string,
) => {
  let resolvedPath = path;
  if (canExecuteAnyAdminOperation()) {
    if (path === "/") {
      resolvedPath = "/admin/transactions";
    }
  } else {
    if ((path === "/" || path.startsWith("/admin")) && activeClientId) {
      resolvedPath = `/clients/${activeClientId}/transactions`;
    }
  }

  return resolvedPath;
};

export function createRootRoute(AppShell: ComponentType) {
  return createRootRouteWithContext<AuthCtx>()({
    component: () => (
      <>
        <HeadContent />
        {/* NOTE We statically exclude Devtools
            from getting in the production bundle. */}
        {import.meta.env.DEV && (
          <>
            <TanStackRouterDevtools />
            <ReactQueryDevtools />
          </>
        )}
        <AppShell />
      </>
    ),
    beforeLoad({ location, context }) {
      let target = location.pathname;
      if (context.user) {
        const pathSegments = location.pathname.split("/");
        const [, firstSegment, secondSegment] = pathSegments;
        const isClientsRoute = firstSegment === "clients" && !!secondSegment;
        const isAdminRoute = firstSegment === "admin";
        const preferredClientId = isClientsRoute ? secondSegment! : null;
        const { canExecuteAnyAdminOperation } = context;

        let resolvedClientId: string | null = null;

        if (preferredClientId) {
          const fromPreferred =
            context.resolveActiveClientId(preferredClientId);
          resolvedClientId = fromPreferred ?? null;
        } else {
          const fromContext = context.resolveActiveClientId();
          if (fromContext) {
            resolvedClientId = fromContext;
          } else if (context.activeClientId) {
            resolvedClientId = context.activeClientId;
          }
        }

        const canExecuteAdmin = canExecuteAnyAdminOperation();

        if (isClientsRoute) {
          if (resolvedClientId && resolvedClientId !== context.activeClientId) {
            context.setActiveClientId(resolvedClientId);
          }
        } else if (isAdminRoute) {
          if (canExecuteAdmin) {
            if (context.activeClientId !== null) {
              context.setActiveClientId(null);
            }
          } else if (
            resolvedClientId &&
            resolvedClientId !== context.activeClientId
          ) {
            context.setActiveClientId(resolvedClientId);
          }
        } else if (
          !canExecuteAdmin &&
          resolvedClientId &&
          resolvedClientId !== context.activeClientId
        ) {
          context.setActiveClientId(resolvedClientId);
        }

        target = resolvePath(
          canExecuteAnyAdminOperation,
          resolvedClientId,
          target,
        );
      }

      if (target !== location.pathname) {
        throw redirect({
          to: target,
          replace: true,
        });
      }
    },
    head: () => ({
      meta: [
        {
          title: "Admin Console",
        },
      ],
    }),
  });
}
